// Tiny TipTap extension that adds a `marginLeft` attribute (in px) to
// paragraph and heading nodes, plus `indent` / `outdent` commands that bump it
// in 24px steps, capped at 6 levels. For list items, use TipTap's
// sinkListItem / liftListItem instead — this extension intentionally only
// affects regular block nodes.
import { Extension } from "@tiptap/core";

const STEP_PX = 24;
const MAX_LEVEL = 6;

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    blockIndent: {
      indentBlock: () => ReturnType;
      outdentBlock: () => ReturnType;
    };
  }
}

export const BlockIndent = Extension.create({
  name: "blockIndent",
  addOptions() {
    return { types: ["paragraph", "heading", "bulletList", "orderedList"] as string[] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types as string[],
        attributes: {
          indent: {
            default: 0,
            parseHTML: (el) => {
              const ml = (el as HTMLElement).style?.marginLeft ?? "";
              const m = /^(\d+(?:\.\d+)?)px$/.exec(ml);
              if (!m) return 0;
              const lvl = Math.round(Number(m[1]) / STEP_PX);
              return Math.max(0, Math.min(MAX_LEVEL, lvl));
            },
            renderHTML: (attrs) => {
              const lvl = Number(attrs.indent) || 0;
              if (lvl <= 0) return {};
              return { style: `margin-left: ${lvl * STEP_PX}px` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    const bump = (delta: number) =>
      ({ state, tr, dispatch }: { state: import("@tiptap/pm/state").EditorState; tr: import("@tiptap/pm/state").Transaction; dispatch: ((tr: import("@tiptap/pm/state").Transaction) => void) | undefined }) => {
        const { from, to } = state.selection;
        let changed = false;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (!(this.options.types as string[]).includes(node.type.name)) return;
          const cur = Number(node.attrs.indent) || 0;
          const next = Math.max(0, Math.min(MAX_LEVEL, cur + delta));
          if (next === cur) return;
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
          changed = true;
        });
        if (changed && dispatch) dispatch(tr);
        return changed;
      };
    return {
      indentBlock:
        () =>
        ({ state, tr, dispatch }) =>
          bump(1)({ state, tr, dispatch }),
      outdentBlock:
        () =>
        ({ state, tr, dispatch }) =>
          bump(-1)({ state, tr, dispatch }),
    };
  },
});
